import axios, { AxiosRequestConfig } from "axios";
import { Device } from "@capacitor/device";
import useAuth from "@/componentables/auth";
import i18n from "@/i18n";
import Toast from "@/componentables/toast";
import StorageService from "@/services/storage.service";
import useTheme from "@/composables/useTheme";
import { useConnectivity } from "@/composables/useConnectivity";

const ANONYMOUS_ID_KEY = "anonymous_id";

let cachedPlatform: string | null = null;

async function resolvePlatform(): Promise<string> {
    if (cachedPlatform) {
        return cachedPlatform;
    }
    try {
        const info = await Device.getInfo();
        cachedPlatform =
            `${info.platform}-${info.operatingSystem ?? "unknown"}`.toLowerCase();
    } catch {
        cachedPlatform = "unknown";
    }
    return cachedPlatform;
}

function generateAnonymousId(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replaceAll(/[xy]/g, (c) => {
        const r = Math.trunc(Math.random() * 16);
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

async function getOrCreateAnonymousId(): Promise<string> {
    let id = await StorageService.get(ANONYMOUS_ID_KEY);
    if (!id || typeof id !== "string") {
        id = generateAnonymousId();
        await StorageService.set(ANONYMOUS_ID_KEY, id);
    }
    return id;
}

const ApiService = {
    _requestInterceptor: 0,
    _401interceptor: 0,
    _anonymousIdInterceptor: 0,
    _themeInterceptor: 0,
    isLoading: false,

    init(baseURL: string | undefined) {
        axios.defaults.baseURL = baseURL;
        axios.defaults.headers.common["Accept-Language"] =
            i18n.global.locale.value;

        this._anonymousIdInterceptor = axios.interceptors.request.use(
            async (config) => {
                const anonymousId = await getOrCreateAnonymousId();
                config.headers["X-Anonymous-Id"] = anonymousId;
                config.headers["X-Platform"] = await resolvePlatform();
                return config;
            },
        );

        // Set up theme interceptor to always read current theme value at request time
        this._themeInterceptor = axios.interceptors.request.use((config) => {
            const { effectiveTheme } = useTheme();
            config.headers["X-Theme-Mode"] = effectiveTheme.value;
            return config;
        });
    },

    async setHeader() {
        const auth = useAuth();

        const token = await auth.getToken();

        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        // Note: X-Theme-Mode is now set via request interceptor to always use current value
    },

    removeHeader() {
        axios.defaults.headers.common = {};
    },

    async get(resource: string, params = {}) {
        await this.setHeader();

        return axios.get(resource, { params }).then((response) => {
            return response;
        });
    },

    async post(
        resource: string,
        data: any,
        headers: AxiosRequestConfig | undefined = undefined,
    ) {
        await this.setHeader();
        return axios.post(resource, data, headers);
    },

    async put(resource: string, data: any) {
        await this.setHeader();
        return axios.put(resource, data);
    },

    async delete(resource: string) {
        await this.setHeader();
        return axios.delete(resource);
    },

    customRequest(data: AxiosRequestConfig) {
        return axios(data);
    },

    // TODO - fix this
    mountRequestInterceptor() {
        this._requestInterceptor = axios.interceptors.request.use(
            async (config) => {
                if (this.isLoading) {
                    return config;
                }

                this.isLoading = true;

                return config;
            },
        );
    },

    mount401Interceptor() {
        const auth = useAuth();
        this._401interceptor = axios.interceptors.response.use(
            async (response) => {
                if (!this.isLoading) {
                    return response;
                }

                this.isLoading = false;

                return response;
            },
            async (error) => {
                if (this.isLoading) {
                    this.isLoading = false;
                }

                if (error?.response?.status === 401) {
                    await auth.logout();
                } else {
                    throw error;
                }
            },
        );
    },

    mountResponseInterceptor() {
        axios.interceptors.response.use(
            (response) => {
                // Any successful response proves the API is reachable again.
                try {
                    useConnectivity().reportApiSuccess();
                } catch {
                    // never break the interceptor chain
                }
                return response;
            },
            async (error) => {
                // Feed connectivity state. Server-down (no-response / 5xx) is owned
                // by the outage banner, so it is intentionally absent from the
                // toast map below — the banner supersedes the generic toast.
                try {
                    const status = error?.response?.status;
                    if (!error.response) {
                        useConnectivity().reportApiError("no-response");
                    } else if (status >= 500) {
                        useConnectivity().reportApiError("5xx");
                    }
                } catch {
                    // never break the interceptor chain
                }

                const { t } = i18n.global;
                const toast = new Toast();
                const errorMessages = {
                    403: t("errors.forbidden"),
                    404: t("errors.not_found"),
                    419: t("errors.page_expired"),
                    429: t("errors.too_many_requests"),
                };

                if (
                    error.response &&
                    Object.keys(errorMessages).includes(
                        error.response.status.toString(),
                    )
                ) {
                    toast.open(
                        errorMessages[
                            error.response.status as keyof typeof errorMessages
                        ],
                    );
                }

                try {
                    const method = error?.config?.method?.toUpperCase() ?? "?";
                    const url = error?.config?.url ?? "unknown";
                    const status = error?.response?.status ?? "no response";
                    let body = "";
                    try {
                        body = error?.response?.data
                            ? JSON.stringify(error.response.data).slice(0, 500)
                            : "";
                    } catch {
                        // circular reference or non-serializable response
                    }
                    console.error(
                        `[API ${status}] ${method} ${url}${body ? " → " + body : ""}`,
                    );
                } catch {
                    // never break the interceptor chain
                }

                if (
                    error?.response?.status === 422 &&
                    error.response.data?.errors
                ) {
                    Object.values(error.response.data.errors).forEach(
                        (errors: any) => {
                            errors.forEach((message: string) => {
                                toast.open(message);
                            });
                        },
                    );
                }

                return Promise.reject(error);
            },
        );
    },

    unmount401Interceptor() {
        axios.interceptors.response.eject(this._401interceptor);
    },
};

export default ApiService;
